import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { performanceAPI, testResultAPI, testCaseAPI } from '../services/api';

function CurrentPerformance() {
  const [starredRuns, setStarredRuns] = useState([]);
  const [aggregatedMetrics, setAggregatedMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [showAggregated, setShowAggregated] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchStarredRuns();
    fetchAggregatedMetrics();
  }, [selectedProject]);

  const fetchProjects = async () => {
    try {
      const response = await testCaseAPI.getProjects();
      setProjects(response.data);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
    }
  };

  const fetchStarredRuns = async () => {
    try {
      setLoading(true);
      const filters = {
        stage: 'performance',
        project: selectedProject || undefined,
        sortBy: 'createdAt',
        sortOrder: 'desc',
        limit: 100
      };
      const response = await testResultAPI.getAll(filters);
      setStarredRuns(response.data.results || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAggregatedMetrics = async () => {
    try {
      const response = await performanceAPI.getAll(selectedProject);
      setAggregatedMetrics(response.data);
    } catch (err) {
      console.error('Failed to fetch aggregated metrics:', err);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 B';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const getSuccessRateBadge = (rate) => {
    if (rate >= 95) return 'badge-success';
    if (rate >= 80) return 'badge-warning';
    return 'badge-danger';
  };

  const getLatencyBadge = (latency) => {
    if (latency < 100) return 'badge-success';
    if (latency < 500) return 'badge-warning';
    return 'badge-danger';
  };

  const calculateScale = (metrics) => {
    if (!metrics.execution_time_ms || metrics.execution_time_ms === 0) return 0;
    const minutes = metrics.execution_time_ms / 60000;
    return Math.round(metrics.total_requests / minutes);
  };

  const getTotalTraffic = (trafficData) => {
    const auto = trafficData?.autoCalculatedTraffic || 0;
    const manual = trafficData?.manualTraffic || 0;
    return auto + manual;
  };

  const getTrafficPerRequest = (trafficData, totalRequests) => {
    const totalTraffic = getTotalTraffic(trafficData);
    if (!totalTraffic || !totalRequests) return 0;
    return (totalTraffic / totalRequests).toFixed(4);
  };

  const getScaleExplanation = (metrics) => {
    if (!metrics.execution_time_ms || metrics.execution_time_ms === 0) return 'N/A';
    const minutes = (metrics.execution_time_ms / 60000).toFixed(2);
    return `${metrics.total_requests} requests ÷ ${minutes} min`;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <h2>Performance Dashboard</h2>
        <p className="text-muted">Starred test runs and aggregated metrics</p>

        {/* Filter */}
        <div className="form-group mb-4">
          <label>Filter by Project</label>
          <select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
            <option value="">All Projects</option>
            {projects.map(project => (
              <option key={project} value={project}>{project}</option>
            ))}
          </select>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        {/* Starred Test Runs */}
        <h3>⭐ Starred Test Runs</h3>
        {starredRuns.length === 0 ? (
          <div className="alert alert-info">
            No starred test runs yet. Promote test results to "performance" stage from the result detail page.
          </div>
        ) : (
          <div className="mb-5">
            {starredRuns.map(run => {
              const scale = calculateScale(run.metrics);
              const totalTraffic = getTotalTraffic(run.trafficData);
              const trafficPerRequest = getTrafficPerRequest(run.trafficData, run.metrics.total_requests);
              const scaleExplanation = getScaleExplanation(run.metrics);

              return (
                <div key={run._id} className="card mb-3" style={{ backgroundColor: '#fff', border: '1px solid #ddd' }}>
                  <div className="flex-between mb-3">
                    <div>
                      <h4 style={{ margin: 0 }}>
                        <Link to={`/result/${run._id}`}>{run.testCaseName}</Link>
                      </h4>
                      <p className="text-muted" style={{ margin: '0.25rem 0 0 0' }}>
                        {run.project} • {new Date(run.createdAt).toLocaleString()}
                        {run.proxyName && ` • Proxy: ${run.proxyName}`}
                      </p>
                    </div>
                  </div>

                  {/* Primary Metrics */}
                  <div className="grid grid-4 mb-3">
                    <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
                        Success Rate
                      </h5>
                      <h3 style={{ margin: 0 }}>
                        <span className={`badge ${getSuccessRateBadge(run.metrics.success_rate)}`}>
                          {run.metrics.success_rate.toFixed(1)}%
                        </span>
                      </h3>
                    </div>

                    <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
                        Avg Latency
                      </h5>
                      <h3 style={{ margin: 0 }}>
                        <span className={`badge ${getLatencyBadge(run.metrics.latency_metrics.avg)}`}>
                          {run.metrics.latency_metrics.avg.toFixed(0)}ms
                        </span>
                      </h3>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#6c757d' }}>
                        P95: {run.metrics.latency_metrics.p95.toFixed(0)}ms
                      </p>
                    </div>

                    <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
                        Scale Capacity
                      </h5>
                      <h3 style={{ margin: 0, color: '#17a2b8' }}>
                        {scale.toLocaleString()}
                      </h3>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#6c757d' }}>
                        requests/min
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic' }}>
                        {scaleExplanation}
                      </p>
                    </div>

                    <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', color: '#6c757d' }}>
                        Traffic
                      </h5>
                      <h3 style={{ margin: 0, color: '#28a745' }}>
                        {totalTraffic.toFixed(2)} MB
                      </h3>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: '#6c757d' }}>
                        {run.trafficData?.autoCalculatedTraffic && run.trafficData?.manualTraffic
                          ? `Auto: ${run.trafficData.autoCalculatedTraffic.toFixed(1)} + Manual: ${run.trafficData.manualTraffic.toFixed(1)}`
                          : run.trafficData?.autoCalculatedTraffic
                            ? 'Auto-calculated'
                            : run.trafficData?.manualTraffic
                              ? 'Manual entry'
                              : 'Not available'}
                      </p>
                    </div>
                  </div>

                  {/* Secondary Metrics */}
                  <div className="grid grid-3">
                    <div className="card" style={{ backgroundColor: '#e9ecef', padding: '0.75rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#6c757d' }}>
                        Traffic per Request
                      </h5>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: '#28a745' }}>
                        {trafficPerRequest} MB
                      </p>
                    </div>

                    <div className="card" style={{ backgroundColor: '#e9ecef', padding: '0.75rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#6c757d' }}>
                        Concurrency
                      </h5>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: '#17a2b8' }}>
                        {run.executionConfig?.concurrency || 'N/A'}
                      </p>
                    </div>

                    <div className="card" style={{ backgroundColor: '#e9ecef', padding: '0.75rem', textAlign: 'center' }}>
                      <h5 style={{ margin: '0 0 0.25rem 0', fontSize: '0.85rem', color: '#6c757d' }}>
                        Rows Executed
                      </h5>
                      <p style={{ margin: 0, fontSize: '1rem', fontWeight: 'bold', color: '#6f42c1' }}>
                        {run.executionConfig?.iterations || run.metrics.total_requests}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Aggregated Metrics (Collapsible) */}
        <div className="mt-5">
          <div className="flex-between mb-3">
            <h3>Aggregated Metrics (Historical)</h3>
            <button
              onClick={() => setShowAggregated(!showAggregated)}
              className="btn btn-sm btn-secondary"
            >
              {showAggregated ? 'Hide' : 'Show'} Aggregated Data
            </button>
          </div>

          {showAggregated && aggregatedMetrics.length > 0 && (
            <div>
              {aggregatedMetrics.map(perf => (
                <div key={perf._id} className="card mb-3" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
                  <h4>
                    <Link to={`/performance/${perf._id}`}>{perf.testCaseName}</Link>
                  </h4>
                  <p className="text-muted">{perf.project} • {perf.endpoint}</p>
                  <div className="grid grid-4">
                    <div>
                      <strong>Success Rate:</strong> {perf.successRate.toFixed(1)}%
                    </div>
                    <div>
                      <strong>Avg Latency:</strong> {perf.avgLatency.toFixed(0)}ms
                    </div>
                    <div>
                      <strong>Capacity:</strong> {perf.requestsPerMinute.toLocaleString()} req/min
                    </div>
                    <div>
                      <strong>Cost:</strong> ${perf.costPerRequest.toFixed(4)}/req
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CurrentPerformance;
